import {
  Box,
  Desktop,
  Heading,
  LazyImageProps,
  Mobile,
  Portal,
  Text,
  useWindowSize,
} from '@miro-site/core'
import React, {useState} from 'react'
import {useTheme} from 'styled-components'
import ActionsOverlay from './components/ActionsOverlay'
import CardDescription from './components/CardDescription'
import CardImage from './components/CardImage'
import {TemplateCardProps} from './TemplateCard'

export interface HighlightedCardProps extends TemplateCardProps {
  templateImageForMobile: LazyImageProps
  position?: number
}

const HighlightedCard: React.FC<HighlightedCardProps> = ({
  boardId,
  templateActions,
  templatePagePath,
  className,
  title,
  description,
  templateImage,
  templateImageForMobile,
  previewButton,
  actionButtonProps,
  accessConstraints,
  appSlug,
  position,
  ...props
}) => {
  const [showDescription, setShowDescription] = useState(false)

  const theme = useTheme()

  const windowSize = useWindowSize()

  const isMobileViewport = windowSize.width && windowSize.width < theme.breakpoints.small

  const [isOverlayVisible, setOverlayVisible] = useState(false)

  return (
    <Box className={className} {...props}>
      <Box
        $styles={{
          position: 'relative',
          height: 0,
          paddingTop: ['64.307%', '59.090%'],
        }}
        onMouseEnter={() => setOverlayVisible(true)}
        onMouseLeave={() => setOverlayVisible(false)}
      >
        <CardImage
          onShowCardDescription={() => setShowDescription(true)}
          image={{...templateImage, width: 550, height: 325}}
          mobileImage={{...templateImageForMobile, width: 325, height: 209}}
          priority={position < 3}
          wrapperStyles={{
            marginBottom: [12, null, null, null],
            borderRadius: [8, 16, 16, 16],
            position: 'absolute',
            overflow: 'hidden',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'grey',
          }}
        />
        <Desktop $block={true}>
          <Heading
            as="div"
            $variant="h4"
            $styles={{
              position: 'absolute',
              maxWidth: 168,
              top: 32,
              left: 32,
              zIndex: 1,
            }}
          >
            {title}
          </Heading>
          <ActionsOverlay
            boardId={boardId}
            templateActions={templateActions}
            templatePagePath={templatePagePath}
            previewButton={previewButton}
            actionButtonProps={actionButtonProps}
            onMoreInfoClick={() => setShowDescription(true)}
            isVisible={isOverlayVisible}
          />
        </Desktop>
        {isOverlayVisible && (
          <>
            {isMobileViewport ? (
              <Portal>
                <CardDescription
                  boardId={boardId}
                  templateActions={templateActions}
                  templatePagePath={templatePagePath}
                  isShown={showDescription}
                  isFullscreen={true}
                  title={title}
                  previewButton={previewButton}
                  actionButtonProps={actionButtonProps}
                  description={description}
                  thumbnail={templateImage}
                  onClose={() => setShowDescription(false)}
                  popupStyles={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    left: 0,
                    height: '100%',
                    overflow: 'auto',
                    zIndex: 701,
                    paddingTop: [64, null, null, null],
                    paddingRight: [20, null, null, null],
                    paddingBottom: [43, null, null, null],
                    paddingLeft: [20, null, null, null],
                  }}
                />
              </Portal>
            ) : (
              <CardDescription
                boardId={boardId}
                templateActions={templateActions}
                templatePagePath={templatePagePath}
                isShown={showDescription}
                previewButton={previewButton}
                actionButtonProps={actionButtonProps}
                title={title}
                description={description}
                thumbnail={templateImage}
                onClose={() => setShowDescription(false)}
                descriptionStyles={{
                  overflow: 'auto',
                  maxHeight: [null, 200, 200, 200],
                }}
                headingStyles={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
                popupStyles={{
                  position: 'absolute',
                  overflow: 'hidden',
                  width: '100%',
                  height: '100%',
                  paddingTop: [null, 40, 40, 40],
                  paddingRight: [null, 167, 167, 167],
                  paddingBottom: [null, 40, 40, 40],
                  paddingLeft: [null, 40, 40, 40],
                  top: 0,
                  left: 0,
                }}
              />
            )}
          </>
        )}
      </Box>
      <Mobile>
        <Text as="div" $variant="p1" $styles={{fontWeight: 700}}>
          {title}
        </Text>
      </Mobile>
    </Box>
  )
}

export default HighlightedCard
